/**
 * Service Worker registration for Marzz AI PWA.
 * Registers the service worker in production and handles updates.
 */

const isLocalhost = Boolean(
  window.location.hostname === 'localhost' ||
    window.location.hostname === '[::1]' ||
    window.location.hostname.match(/^127(?:\.(?:25[0-5]|2[0-4]\d|[01]?\d\d?)){3}$/),
)

type Config = {
  onSuccess?: (registration: ServiceWorkerRegistration) => void
  onUpdate?: (registration: ServiceWorkerRegistration) => void
}

export function register(config?: Config) {
  if (process.env.NODE_ENV === 'production' && 'serviceWorker' in navigator) {
    const publicUrl = new URL(process.env.PUBLIC_URL || '', window.location.href)
    if (publicUrl.origin !== window.location.origin) {
      // SW won't work if PUBLIC_URL is on a different origin
      return
    }

    window.addEventListener('load', () => {
      const swUrl = `${process.env.PUBLIC_URL}/sw.js`

      if (isLocalhost) {
        // Running on localhost — check if SW exists, then register
        checkValidServiceWorker(swUrl, config)
        navigator.serviceWorker.ready.then(() => {
          console.log('[Marzz AI] Service worker ready (localhost)')
        })
      } else {
        // Production — register directly
        registerValidSW(swUrl, config)
      }
    })
  }
}

function registerValidSW(swUrl: string, config?: Config) {
  navigator.serviceWorker
    .register(swUrl)
    .then(registration => {
      registration.onupdatefound = () => {
        const installingWorker = registration.installing
        if (!installingWorker) return

        installingWorker.onstatechange = () => {
          if (installingWorker.state === 'installed') {
            if (navigator.serviceWorker.controller) {
              // New content available — notify and auto-refresh
              console.log('[Marzz AI] New version available, updating...')

              if (config?.onUpdate) {
                config.onUpdate(registration)
              } else {
                // Auto-update: tell the waiting SW to activate, then reload
                if (registration.waiting) {
                  registration.waiting.postMessage({ type: 'SKIP_WAITING' })
                }
              }
            } else {
              // First install — content cached for offline
              console.log('[Marzz AI] Content cached for offline use.')
              config?.onSuccess?.(registration)
            }
          }
        }
      }
    })
    .catch(error => {
      console.error('[Marzz AI] Service worker registration failed:', error)
    })
}

function checkValidServiceWorker(swUrl: string, config?: Config) {
  fetch(swUrl, { headers: { 'Service-Worker': 'script' } })
    .then(response => {
      const contentType = response.headers.get('content-type')
      if (response.status === 404 || (contentType && !contentType.includes('javascript'))) {
        // No valid SW found — unregister and reload
        navigator.serviceWorker.ready.then(registration => {
          registration.unregister().then(() => window.location.reload())
        })
      } else {
        registerValidSW(swUrl, config)
      }
    })
    .catch(() => {
      console.log('[Marzz AI] No internet connection. App running in offline mode.')
    })
}

export function unregister() {
  if ('serviceWorker' in navigator) {
    navigator.serviceWorker.ready
      .then(registration => registration.unregister())
      .catch(error => console.error(error))
  }
}

// Listen for controller change and reload to get the latest version (production only)
if (process.env.NODE_ENV === 'production' && 'serviceWorker' in navigator) {
  let refreshing = false
  navigator.serviceWorker.addEventListener('controllerchange', () => {
    if (!refreshing) {
      refreshing = true
      window.location.reload()
    }
  })
}
